import { getRouteApi } from '@tanstack/react-router';
import { Card, CardContent } from '@ui/card.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import { useTranslation } from 'react-i18next';
import { ShopDetailsGrid } from '@admin/components/shops/shop-details-grid.tsx';
import { ShopProfileHeader } from '@admin/components/shops/shop-profile-header.tsx';
import { CustomShopCatalog } from '@admin/components/shops/custom-shop-catalog.tsx';
import { CustomShopBrandsCatalog } from '@admin/components/shops/custom-shop-brands-catalog.tsx';
import { CustomShopCategoriesCatalog } from '@admin/components/shops/custom-shop-categories-catalog.tsx';
import { EntitySegmentsCard } from '@admin/components/segments/entity-segments-card.tsx';
import {
  useApiShopsIdGet,
  useApiAdminshopWorkspacesPost,
  getApiShopsIdGetQueryKey,
  getApiShopsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import {
  ShopShopReadModelType,
  type ShopWorkspaceShopWorkspaceWriteModel
} from '@r/api/generated/client.schemas.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isRewardShopInStepProgram } from '@admin/components/shops/shop-reward-step.ts';

const ShopSkeleton = () => (
  <Card className="w-full">
    <CardContent className="p-6">
      <div className="flex flex-col gap-6 md:flex-row">
        <div className="flex flex-col items-center space-y-2">
          <Skeleton className="w-24 h-24 rounded-full" />
          <Skeleton className="w-32 h-6" />
          <Skeleton className="w-24 h-4" />
        </div>
        <div className="flex-1 space-y-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[...Array(2)].map((_, subIndex) => (
                <div key={subIndex} className="space-y-2">
                  <Skeleton className="w-24 h-4" />
                  <Skeleton className="w-full h-10" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const Shop = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { id: shopId } = getRouteApi('/auth-layout/shops/$id').useParams();
  const queryClient = useQueryClient();

  const user = useUser();
  const { workspace } = UseWorkspace(user.data);
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const logicType = workspace?.currentLogicWorkspace?.logic?.type;
  const canEditShopInfo =
    activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN') ||
    activeWorkspace?.roles?.includes('ROLE_ADMIN') ||
    false;

  const shopQuery = useApiShopsIdGet(shopId);
  useOrvalQueryErrorToast(shopQuery, t('toast.error.label'));

  const { data: shop, isLoading, isSuccess } = shopQuery;

  const hasTriedCreateRef = useRef(false);
  const [isEditShopOpen, setIsEditShopOpen] = useState(false);
  const [customLogoPreview, setCustomLogoPreview] = useState<string>();

  const {
    mutate: createShopWorkspace,
    isPending: isCreatingShopWorkspace,
    isError: isCreateShopWorkspaceError
  } = useApiAdminshopWorkspacesPost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getApiShopsIdGetQueryKey(shopId) });
        queryClient.invalidateQueries({ queryKey: getApiShopsGetCollectionQueryKey() });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('shop.details.error'));
      }
    }
  });

  useEffect(() => {
    hasTriedCreateRef.current = false;
  }, [shopId]);

  /** Crée automatiquement le shopWorkspace s'il n'existe pas encore pour cette boutique. */
  useEffect(() => {
    if (
      isSuccess &&
      shop &&
      !shop.shopWorkspace &&
      !isCreatingShopWorkspace &&
      !hasTriedCreateRef.current
    ) {
      hasTriedCreateRef.current = true;
      const payload: ShopWorkspaceShopWorkspaceWriteModel = {
        shop: `/shops/${shopId}`
      };
      createShopWorkspace({ data: payload });
    }
  }, [createShopWorkspace, isCreatingShopWorkspace, isSuccess, shop, shopId]);

  const isPreparingShopWorkspace =
    isSuccess &&
    shop &&
    !shop.shopWorkspace &&
    !isCreateShopWorkspaceError &&
    (!hasTriedCreateRef.current || isCreatingShopWorkspace);

  const rewardStepLayout =
    isSuccess && shop ? isRewardShopInStepProgram(shop.type, logicType) : false;

  if (isLoading || isPreparingShopWorkspace) {
    return (
      <div className="space-y-5">
        <h1 className="text-4xl font-bold">{t('admin.shop.title')}</h1>
        <ShopSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {isSuccess && shop && shop.shopWorkspace ? (
        <>
          <ShopProfileHeader
            shop={{
              displayName: shop.displayName,
              logoUri: customLogoPreview ?? shop.logo?.pathUri ?? undefined
            }}
            isActive={shop.shopWorkspace.isActive ?? false}
          />

          <ShopDetailsGrid
            shop={{ ...shop, shopWorkspace: shop.shopWorkspace }}
            isEditOpen={isEditShopOpen}
            onEditOpenChange={setIsEditShopOpen}
            onLogoPreviewChange={setCustomLogoPreview}
            canEdit={canEditShopInfo}
            rewardStepLayout={rewardStepLayout}
          />
        </>
      ) : (
        t('shop.details.error')
      )}

      {isSuccess && shop && (
        <>
          {shop.type === ShopShopReadModelType.custom && (
            <>
              <CustomShopBrandsCatalog shopId={shop.id} />
              <CustomShopCategoriesCatalog shopId={shop.id} />
              <CustomShopCatalog shopId={shop.id} />
            </>
          )}
          {rewardStepLayout && <CustomShopCatalog shopId={shop.id} />}
          {shop.shopWorkspace && !rewardStepLayout && (
            <EntitySegmentsCard entityType="shop" entityId={shop.id} />
          )}
        </>
      )}
    </div>
  );
};
